import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, PillTab, DateTimePicker, Counter, GroupedInputBox, LabeledField, Select, SEO } from '../../../components/common';
import TransferLanding from '../../../components/service/TransferLanding';
import { useSearchFlow, SearchFlowModals } from '../../../components/service/SearchFlow';
import SearchButton from '../../../components/service/SearchButton';
import AirportSelector from '../../../components/service/AirportSelector';
import AddressSearch from '../../../components/service/AddressSearch';
import { destinationService } from '../../../api/services';
import useHolidayWarning from '../../../hooks/useHolidayWarning';
import HolidayWarningModal from '../../../components/service/HolidayWarningModal';
import { useFieldGuide } from '../../../hooks/useFieldGuide';
import { useDeepLTranslated } from '../../../hooks/useDeepLTranslated';
import { loadRecentSearch, saveRecentSearch } from '../../../hooks/useRecentSearch';
import { usePendingSearch } from '../../../hooks/usePendingSearch';
import { getDefaultBookingDateTime } from '../../../utils/dateUtils';

const FIELDS = [0, 1, 2, 3];

export default function TransferAirport() {
  const { t } = useTranslation();
  // #101: hover tooltip 제거 — 아래 상시 뱃지가 이미 동일 정보 표시 (tooltip이 옆 탭/서비스 가리는 문제)
  const directionTabs = [
    { id: 'from-airport', label: t('form.from_airport') },
    { id: 'to-airport', label: t('form.to_airport') },
  ];
  const location = useLocation();
  const flow = useSearchFlow('airport');
  const { isHighlighted, onFocusField, wrapChange, resetGuide } = useFieldGuide(FIELDS);
  const recent = loadRecentSearch('airport', 4);
  // #103: 쇼핑 페이지의 "이동 예약하기"에서 넘어올 때 navigate state로 전달된 direction 적용
  const initialDirection = location.state?.formData?.direction === 'to-airport' ? 'to-airport' : 'from-airport';
  const [direction, setDirection] = useState(initialDirection);
  const [departure, setDeparture] = useState('');
  const [destination, setDestination] = useState('');
  // 사용자가 검색한 장소명(예: "은마아파트") — 주소 외에 명칭도 보관해서 booking 화면에서 표시
  const [searchedDepartureName, setSearchedDepartureName] = useState('');
  const [searchedDestinationName, setSearchedDestinationName] = useState('');
  // #216 후속: 표시 전용 현지화 명칭. mobile/desktop AddressSearch 가 별도 인스턴스라
  // displayName 으로 공유해야 모드 전환 시 raw 한국어 주소로 회귀하지 않음. BE 전송값(name)은 raw 유지.
  const [searchedDepartureNameI18n, setSearchedDepartureNameI18n] = useState('');
  const [searchedDestinationNameI18n, setSearchedDestinationNameI18n] = useState('');
  const [dateTime, setDateTime] = useState(() => getDefaultBookingDateTime());
  const [people, setPeople] = useState(4);
  const [airportOptions, setAirportOptions] = useState([]);
  // #36: 무료 대기 뱃지 분 수를 어드민 OnsiteSurchargeTab 입력값으로 동적 표시.
  //      from-airport(픽업) / to-airport(샌딩) 별로 분리.
  const [freeWaitMinutes, setFreeWaitMinutes] = useState({ pickup: 60, sending: 30 });
  useEffect(() => {
    destinationService.getBufferSettings('airport').then((rows) => {
      const list = Array.isArray(rows) ? rows : [];
      const pickup = list.find((b) => b.direction === 'pickup');
      const sending = list.find((b) => b.direction === 'sending');
      setFreeWaitMinutes({
        pickup: pickup?.free_waiting_minutes ?? 60,
        sending: sending?.free_waiting_minutes ?? 30,
      });
    }).catch(() => {});
  }, []);

  const { holidayInfo } = useHolidayWarning(dateTime);
  const [holidayModal, setHolidayModal] = useState(null);
  const pendingSearchRef = useRef(null);

  // Restore pending search data after login redirect
  usePendingSearch('airport', useCallback((data) => {
    if (data.departure) setDeparture(data.departure);
    if (data.destination) setDestination(data.destination);
    if (data.dateTime) setDateTime(data.dateTime);
    if (data.people) setPeople(data.people);
    if (data.direction) setDirection(data.direction);
  }, []));

  useEffect(() => {
    destinationService.getAirports().then((data) => {
      // 모바일 Select 라벨 다국어: AirportSelector와 동일 패턴 — 먼저 i18n 키 `common.airport_{code}` 매칭,
      // 매칭 실패 시 원본 한국어 name 을 남겨두고 아래 useDeepLTranslated 훅으로 DeepL 보정.
      setAirportOptions(data.map((a) => {
        const i18nKey = `common.airport_${a.code}`;
        const translated = t(i18nKey);
        const label = translated !== i18nKey ? translated : a.name;
        return { value: a.code, label, address: a.address };
      }));
    }).catch((e) => console.warn('[Airport] 공항 목록 로드 실패:', e.message));
    // 무료대기 정보는 PillTab tooltip으로 표시 (freeRows 배지 미사용)
  }, [t]);

  // i18n 매칭 실패로 한국어가 남은 라벨은 DeepL로 보정 (모바일 Select 전용)
  const airportLabelBundle = useMemo(() => airportOptions.map((a) => a.label), [airportOptions]);
  const tAirportLabels = useDeepLTranslated(airportLabelBundle);
  const tAirportOptions = useMemo(
    () => airportOptions.map((a, i) => ({ ...a, label: tAirportLabels[i] || a.label })),
    [airportOptions, tAirportLabels],
  );

  const handleDirectionChange = (id) => {
    setDirection(id);
    setDeparture('');
    setDestination('');
    setSearchedDepartureName('');
    setSearchedDestinationName('');
    resetGuide();
  };

  return (
    <>
    <SEO serviceType="Airport Transfer" renderH1 />
    <TransferLanding heroImg="airport">
      {/* Direction Buttons + #101: 활성 탭 바로 아래에 무료대기 뱃지 (다른 버튼 안 가리도록 명확한 줄 분리) */}
      <div className="flex flex-col gap-1.5">
        <PillTab
          tabs={directionTabs}
          activeTab={direction}
          onTabChange={handleDirectionChange}
        />
        <div className="inline-flex items-center self-start gap-1 px-2.5 py-1 rounded-full bg-orange-50 text-[11px] sm:text-xs text-orange-500 font-semibold">
          <Icon name="time" size={11} className="text-orange-500" />
          {t('booking.free_waiting_minutes', { minutes: direction === 'from-airport' ? freeWaitMinutes.pickup : freeWaitMinutes.sending })}
        </div>
      </div>

      {/* Row 1: 출발지 + 도착지 — Mobile (Select fallback) */}
      <GroupedInputBox className="lg:hidden">
        {direction === 'from-airport' ? (
          <Select value={departure} onChange={setDeparture} placeholder={t('form.select_origin_airport')} options={tAirportOptions} variant="borderless" />
        ) : (
          <AddressSearch value={departure} displayName={searchedDepartureNameI18n} onChange={(address, name, nameI18n) => { setDeparture(address); setSearchedDepartureName(name || ''); setSearchedDepartureNameI18n(nameI18n || name || ''); }} placeholder={t('form.airport_departure_placeholder')} variant="borderless" restriction={{ message: t('form.region_restriction_seoul'), allowGyeonggi: true, allowIncheon: true }} />
        )}
        {direction === 'from-airport' ? (
          <AddressSearch value={destination} displayName={searchedDestinationNameI18n} onChange={(address, name, nameI18n) => { setDestination(address); setSearchedDestinationName(name || ''); setSearchedDestinationNameI18n(nameI18n || name || ''); }} placeholder={t('form.airport_destination_placeholder')} variant="borderless" restriction={{ message: t('form.region_restriction_seoul'), allowGyeonggi: true, allowIncheon: true }} />
        ) : (
          <Select value={destination} onChange={setDestination} placeholder={t('form.select_arrival_airport')} options={tAirportOptions} variant="borderless" />
        )}
      </GroupedInputBox>

      {/* Row 1: Desktop */}
      <div className="hidden lg:flex lg:items-start gap-2.5">
        <LabeledField label={t('form.departure')} highlighted={isHighlighted(0)} onFocusCapture={() => onFocusField(0)} hint={direction === 'to-airport' ? t('form.region_restriction_seoul') : undefined}>
          {direction === 'from-airport' ? (
            <AirportSelector
              value={departure}
              onChange={(val) => { wrapChange(0, setDeparture)(val); }}
              placeholder={t('form.select_origin_airport')}
            />
          ) : (
            <AddressSearch
              value={departure}
              displayName={searchedDepartureNameI18n}
              onChange={(address, name, nameI18n) => { wrapChange(0, setDeparture)(address); setSearchedDepartureName(name || ''); setSearchedDepartureNameI18n(nameI18n || name || ''); }}
              placeholder={t('form.airport_departure_placeholder')}
              restriction={{ message: t('form.region_restriction_seoul'), allowGyeonggi: true, allowIncheon: true }}
            />
          )}
        </LabeledField>
        <div className="flex items-center justify-center w-6 h-12 mt-6">
          <Icon name="arrow-oneway" size={24} className="text-mono-300" />
        </div>
        <LabeledField label={t('form.destination')} highlighted={isHighlighted(1)} onFocusCapture={() => onFocusField(1)} hint={direction === 'from-airport' ? t('form.region_restriction_seoul') : undefined}>
          {direction === 'from-airport' ? (
            <AddressSearch
              value={destination}
              displayName={searchedDestinationNameI18n}
              onChange={(address, name, nameI18n) => { wrapChange(1, setDestination)(address); setSearchedDestinationName(name || ''); setSearchedDestinationNameI18n(nameI18n || name || ''); }}
              placeholder={t('form.airport_destination_placeholder')}
              restriction={{ message: t('form.region_restriction_seoul'), allowGyeonggi: true, allowIncheon: true }}
            />
          ) : (
            <AirportSelector
              value={destination}
              onChange={(val) => { wrapChange(1, setDestination)(val); }}
              placeholder={t('form.select_arrival_airport')}
            />
          )}
        </LabeledField>
      </div>

      {/* Row 2: 차량 이용 일시 + 이용 인원 */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-11">
        <LabeledField label={t('form.vehicle_use_datetime')} highlighted={isHighlighted(2)} onFocusCapture={() => onFocusField(2)}>
          <DateTimePicker value={dateTime} onChange={wrapChange(2, setDateTime)} maxMonths={2} blockHolidays />
        </LabeledField>
        <LabeledField label={t('form.use_passengers')} highlighted={isHighlighted(3)} onFocusCapture={() => onFocusField(3)}>
          <Counter value={people} onChange={wrapChange(3, setPeople)} />
        </LabeledField>
      </div>

      <SearchButton onClick={() => {
        if (!departure) return alert(t('form.alert_select_departure'));
        if (!destination) return alert(t('form.alert_select_destination'));
        if (!dateTime) return alert(t('form.alert_select_datetime'));
        // BugHerd #87 회귀 fix: blocked + surcharge 두 경우 모두 모달 노출 (이전엔 blocked 만).
        const proceed = () => {
          saveRecentSearch('airport', { departure, destination, dateTime, people, direction });
          const resolveAirport = (code) => {
            const translated = tAirportOptions.find((a) => a.value === code);
            const raw = airportOptions.find((a) => a.value === code);
            // BugHerd #85 후속: address 가 없을 때 label(번역) 로 fallback 하면 ZH/JA/ES 모드에서
            // 한국어/영어 키워드 없는 값(仁川国际机场 등)이 BE 로 가서 방향 판별 실패 → 가격 회귀.
            // 공항 코드(ICN/GMP) 는 language-neutral 하고 BE 가 인식하므로 code 를 우선 fallback.
            return { address: raw?.address || code, name: translated?.label || raw?.label || code };
          };
          const dep = direction === 'from-airport' ? resolveAirport(departure) : { address: departure, name: searchedDepartureName };
          const dest = direction === 'to-airport' ? resolveAirport(destination) : { address: destination, name: searchedDestinationName };
          flow.startSearch(dateTime, { direction }, {
            departure: dep.address, departureName: dep.name,
            destination: dest.address, destinationName: dest.name,
            people, direction,
          });
        };
        if (holidayInfo?.blocked) { setHolidayModal(holidayInfo); return; }
        if (holidayInfo?.amount > 0) {
          pendingSearchRef.current = proceed;
          setHolidayModal(holidayInfo);
          return;
        }
        proceed();
      }} />
    </TransferLanding>

    <SearchFlowModals flow={flow} dateTime={dateTime} />
    {holidayModal && <HolidayWarningModal
      holiday={holidayModal}
      onClose={() => setHolidayModal(null)}
      onConfirm={() => { const fn = pendingSearchRef.current; pendingSearchRef.current = null; fn?.(); }}
    />}
    </>
  );
}
